/**
 * src/stores/useCatalogSyncStore.ts
 *
 * HARDEN-02: Non-persisted Zustand store for catalog sync (mirrors useSyncStore shape).
 *
 * flush() reads the catalog offline queue (catalogQueue.ts), applies LWW-by-serviceId,
 * then issues the corresponding catalog API calls for each surviving record.
 * Called at login (adjacent to useSyncStore.flush() in useAuth) and on
 * the window 'online' event (mid-session reconnect via useAuth useEffect).
 *
 * useSyncStore MUST NOT be refactored — isReallyOnline() is COPIED here per HARDEN-02
 * isolation requirement (do NOT extract to shared lib in this plan).
 */

import { create } from 'zustand';
import { peekCatalogQueue, dequeueCatalogFlushed } from '@/shared/lib/catalogQueue';

interface CatalogSyncState {
  status: 'idle' | 'syncing';
  flush: () => Promise<void>;
}

/**
 * Lightweight connectivity probe.
 * Returns false on captive portal / LAN without internet (T-28-02-04 mitigation).
 *
 * COPIED from useSyncStore.ts (HARDEN-02 keeps this store self-contained;
 * do NOT refactor the shipped sync path).
 */
async function isReallyOnline(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
  try {
    const res = await fetch(`/api/health?r=${Math.random()}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: AbortSignal.timeout(3000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export const useCatalogSyncStore = create<CatalogSyncState>()((set) => ({
  status: 'idle',

  flush: async () => {
    // 1. Peek at the catalog queue — if empty, no-op
    const queue = await peekCatalogQueue();
    if (queue.length === 0) return;

    // 2. Probe real connectivity — captive portal mitigation (T-28-02-04)
    const online = await isReallyOnline();
    if (!online) {
      // Leave queue intact; flush will retry on next reconnect / login
      return;
    }

    // 3. Sort by timestamp ASC — oldest first (wall-clock order within different services)
    const sorted = [...queue].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );

    // 4. Apply LWW per serviceId (belt-and-suspenders; enqueue already deduped,
    //    but concurrent tabs or IDB-failure recovery could leave duplicates)
    const lwwMap = new Map<string, (typeof sorted)[0]>();
    for (const r of sorted) {
      lwwMap.set(r.serviceId, r); // last entry for this serviceId wins
    }
    const toFlush = [...lwwMap.values()];

    set({ status: 'syncing' });
    const flushedIds = new Set<string>();

    // 5. Issue fetch per record — one failure does NOT abort the loop
    for (const record of toFlush) {
      try {
        let res: Response;

        if (record.operation === 'upsert_override') {
          // PUT /api/catalog/overrides — mirrors syncOverrideToServer PUT branch
          res = await fetch('/api/catalog/overrides', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            signal: AbortSignal.timeout(10_000),
            body: JSON.stringify(record.payload),
          });
        } else if (record.operation === 'delete_override') {
          // DELETE /api/catalog/overrides/:serviceId — mirrors syncOverrideToServer DELETE branch
          res = await fetch(
            `/api/catalog/overrides/${encodeURIComponent(record.serviceId)}`,
            {
              method: 'DELETE',
              signal: AbortSignal.timeout(10_000),
            },
          );
        } else if (record.operation === 'upsert_custom') {
          // PUT /api/catalog/services/:id — mirrors syncCustomServiceToServer
          res = await fetch(
            `/api/catalog/services/${encodeURIComponent(record.serviceId)}`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              signal: AbortSignal.timeout(10_000),
              body: JSON.stringify(record.payload),
            },
          );
        } else {
          // delete_custom: DELETE /api/catalog/services/:id
          res = await fetch(
            `/api/catalog/services/${encodeURIComponent(record.serviceId)}`,
            {
              method: 'DELETE',
              signal: AbortSignal.timeout(10_000),
            },
          );
        }

        // Only dequeue if server confirmed receipt (2xx)
        if (res.ok) {
          flushedIds.add(record.id);
        }
        // Non-2xx: leave in queue for retry on next reconnect
      } catch {
        // Network/timeout error: leave record in queue for next flush
      }
    }

    // 6. Dequeue only after all fetch attempts — atomic, id-based
    await dequeueCatalogFlushed(flushedIds);
    set({ status: 'idle' });
  },
}));
